import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable, SearchFilterBar, type ColumnDef, type SortState } from '@shared-ui/components';
import { adminApi } from '../../../api/adminApi';
import type { UserFeatureGateView } from '../../../api/userApi';

/**
 * Per-user feature gate overrides.
 *
 * Each gate is tri-state: inherit the global/ring resolution, or pin it ON/OFF
 * for this user. Edits are staged and applied with an explicit Save, as every
 * other admin edit form does — the PUT replaces the whole override map, so a
 * per-toggle autosave would make a mis-click a silent write.
 *
 * Two kinds of row are locked, for different reasons: a gate whose definition
 * sets `allowOverride: false` is a kill switch, and a `global` gate is read with
 * no user in hand at all (the login screen asks before anyone has signed in), so
 * there is nothing for an override here to attach to. The server refuses both.
 */

interface FeatureGatesTabProps {
  userId: string;
}

type Override = 'inherit' | 'on' | 'off';

/** Sort a pinned override above an inherited one, and ON above OFF — the
 *  interesting value first, so one click on the column surfaces it. */
const OVERRIDE_RANK: Record<Override, number> = { on: 0, off: 1, inherit: 2 };

/** Which layer decided the row — the same order the server resolves in. A
 *  kill switch or a global gate ignores both overrides. */
type Source = 'override' | 'tenant' | 'global';
const SOURCE_RANK: Record<Source, number> = { override: 0, tenant: 1, global: 2 };
function sourceOf(g: UserFeatureGateView): Source {
  if (g.resolution === 'global' || g.allowOverride === false) return 'global';
  if (g.userOverride !== null) return 'override';
  if (g.tenantOverride !== null && g.tenantOverride !== undefined) return 'tenant';
  return 'global';
}

function toOverride(value: boolean | null): Override {
  return value === null ? 'inherit' : value ? 'on' : 'off';
}

export function FeatureGatesTab({ userId }: FeatureGatesTabProps) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['userFeatureGates', userId],
    queryFn: () => adminApi.getUserFeatureGates(userId),
    enabled: !!userId,
  });

  const gates = useMemo(() => data?.gates ?? [], [data]);

  const saved = useMemo(() => {
    const map: Record<string, Override> = {};
    for (const g of gates) map[g.name] = toOverride(g.userOverride);
    return map;
  }, [gates]);

  const [draft, setDraft] = useState<Record<string, Override>>({});
  useEffect(() => { setDraft(saved); }, [saved]);

  const [filters, setFilters] = useState<Record<string, string>>({ search: '' });
  const [sort, setSort] = useState<SortState>({ sortBy: 'name', sortOrder: 'asc' });

  const save = useMutation({
    mutationFn: () => {
      // Only pinned gates go in the map — "inherit" means *absent*, which is
      // also how a retired gate's stale override gets pruned.
      const featureGates: Record<string, boolean> = {};
      for (const [name, state] of Object.entries(draft)) {
        if (state !== 'inherit') featureGates[name] = state === 'on';
      }
      return adminApi.updateUserFeatureGates(userId, featureGates);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['userFeatureGates', userId] }),
  });

  /* Name AND description. The description is where the answer usually is —
     "impersonat", "canvas", "re-teaching" — while a gate's name is a slug that
     rarely contains the word anyone has in mind. */
  const needle = (filters.search ?? '').trim().toLowerCase();
  const rows = useMemo(() => (
    needle
      ? gates.filter(g => `${g.name} ${g.description ?? ''}`.toLowerCase().includes(needle))
      : gates
  ), [gates, needle]);

  const columns: ColumnDef<UserFeatureGateView>[] = useMemo(() => [
    {
      key: 'name', header: 'Gate', sortable: true, hideable: false,
      sortValue: g => g.name,
      render: g => (
        <>
          <strong style={{ fontFamily: 'monospace' }}>{g.name}</strong>
          {g.description && (
            <>
              <br />
              <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{g.description}</span>
            </>
          )}
        </>
      ),
    },
    {
      key: 'resolved', header: 'Resolved', sortable: true,
      sortValue: g => (g.resolved ? 0 : 1),
      render: g => (
        <span style={{ fontWeight: 600, color: g.resolved ? 'var(--color-success)' : 'var(--text-tertiary)' }}>
          {g.resolved ? 'ON' : 'OFF'}
        </span>
      ),
    },
    {
      key: 'source', header: 'Source', sortable: true,
      sortValue: g => SOURCE_RANK[sourceOf(g)],
      render: g => (
        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
          {sourceOf(g) === 'tenant' ? `tenant${data?.tenantId ? ` (${data.tenantId})` : ''}` : sourceOf(g)}
        </span>
      ),
    },
    {
      key: 'global', header: 'Global', sortable: true,
      sortValue: g => (g.globalEnabled ? 0 : 1),
      render: g => (g.globalEnabled ? 'ON' : 'OFF'),
    },
    {
      /* Sorted on the SAVED override, never the draft: sorting on what the
         select currently shows would make a row leap out from under the cursor
         the moment it was changed, which is the one thing a staged-edit table
         must not do. */
      key: 'override', header: 'User Override', sortable: true,
      sortValue: g => OVERRIDE_RANK[saved[g.name] ?? 'inherit'],
      render: g => {
        const current = draft[g.name] ?? 'inherit';
        const global = g.resolution === 'global';
        const locked = global || g.allowOverride === false;
        const changed = current !== saved[g.name];
        return (
          <select
            value={current}
            disabled={locked}
            title={global
              ? 'Read before anyone signs in — there is no user to override it for'
              : locked ? 'Kill switch — this gate ignores per-user overrides' : undefined}
            onChange={e => setDraft(d => ({ ...d, [g.name]: e.target.value as Override }))}
            style={{ fontSize: 12, borderColor: changed ? 'var(--color-primary)' : undefined }}
          >
            <option value="inherit">— inherit —</option>
            <option value="on">ON</option>
            <option value="off">OFF</option>
          </select>
        );
      },
    },
  ], [draft, saved, data?.tenantId]);

  if (isLoading) return <div className="loading" style={{ padding: 16 }}>Loading feature gates...</div>;

  if (gates.length === 0) {
    return <p className="no-features-message">No feature gates defined. Add them in Settings &rarr; Feature Gates.</p>;
  }

  /* Dirty is about the whole draft, not the rows on screen: a search that hides
     an edited row must not take the Save button away with it. */
  const dirty = gates.some(g => (draft[g.name] ?? 'inherit') !== saved[g.name]);

  return (
    <div className="features-section">
      {/* No filters of its own and no Fetch: the gates are already in hand, so
          the search narrows them as it is typed rather than after a commit.
          Save rides in `trailingActions`, the bar's own right edge. */}
      <SearchFilterBar
        filters={[]}
        values={filters}
        onChange={setFilters}
        hideFetch
        searchPlaceholder="Search gates…"
        trailingActions={(
          <>
            {save.isError && (
              <span style={{ color: 'var(--color-error)', fontSize: 12 }}>
                {(save.error as Error).message}
              </span>
            )}
            {!dirty && save.isSuccess && (
              <span style={{ color: 'var(--color-success)', fontSize: 12 }}>Saved</span>
            )}
            {dirty && !save.isPending && (
              <button className="btn" onClick={() => setDraft(saved)}>Discard</button>
            )}
            <button className="btn btn-primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : 'Save overrides'}
            </button>
          </>
        )}
      />

      <DataTable
        columns={columns}
        data={rows}
        rowKey={g => g.name}
        sort={sort}
        onSortChange={setSort}
        emptyMessage={needle ? `No gate matches “${filters.search}”.` : 'No feature gates defined.'}
      />
    </div>
  );
}
